import {
	CONNECTION_TYPE,
	DOCS_TYPE,
	IConnectionOperation,
	IDocumentsDocs,
	IDocumentsOperation,
	IOperation,
	OPERATION_TYPE,
} from '../../operations/models/operations.models';
import { IWorkflowDetailResponse } from '../models/workflows.models';

/**
 * Utilidades DOCUMENTS del editor de workflows (NIF-530).
 *
 * Abrir cuando: normalizar `method` both↔array, particionar filas docs/conexiones,
 * o localizar la op DOCUMENTS (sin SCRIPTURE) en el detalle.
 * Sin dependencias Angular — puro y testeable.
 */

/**
 * API `method: 'both'` → form `['manual','photo']`; un solo método → array de uno.
 *
 * @param method - Valor del API (`both` | `manual` | `photo` | …)
 */
export function normalizeDocumentMethodForForm(method: string | undefined): string[] {
	if (method === 'both') {
		return ['manual', 'photo'];
	}
	return [method as string];
}

/**
 * Form array de métodos → API: 2 ítems → `both`; si no, el primero (soporta ISelect).
 *
 * @param method - Array del form (`string` o `{ id }`)
 */
export function serializeDocumentMethodForApi(
	method: Array<string | { id?: string }> | null | undefined,
): string {
	if (!method?.length) {
		return 'manual';
	}
	if (method.length === 2) {
		return 'both';
	}
	const first = method[0];
	if (typeof first === 'object' && first && 'id' in first) {
		return first.id ?? 'manual';
	}
	return String(first);
}

/**
 * Parte filas del FormArray DOCUMENTS para el payload:
 * - `onlyDocs`: sin `connection_available`
 * - `connectionsWithoutEval`: con conexión posible pero no elegidos en eval financiera
 *
 * @param docs - `host.docsAvailables`
 * @param connections - Tipos elegidos en CONNECTIONS (eval)
 */
export function partitionDocumentRowsForPayload(
	docs: IDocumentsDocs[] | null | undefined,
	connections: CONNECTION_TYPE[] | null | undefined,
): { onlyDocs: IDocumentsDocs[]; connectionsWithoutEval: IDocumentsDocs[] } {
	const connectionList = connections ?? [];
	const onlyDocs =
		docs?.filter((doc: IDocumentsDocs) => !doc.docInfo?.confRecommended?.connection_available) ?? [];
	const connectionsWithoutEval =
		docs?.filter(
			(doc: IDocumentsDocs) =>
				doc.docInfo?.confRecommended?.connection_available &&
				!connectionList.includes(doc.docInfo?.confRecommended?.connection_type),
		) ?? [];
	return { onlyDocs, connectionsWithoutEval };
}

/**
 * DOCUMENTS del detalle que no son solo SCRIPTURE (poderes).
 *
 * @param detail - Detalle del workflow
 */
export function findNonScriptureDocumentsOperation(
	detail: Pick<IWorkflowDetailResponse, 'operations'> | undefined,
): IDocumentsOperation | undefined {
	return detail?.operations?.find(
		(op: IOperation) =>
			op.type === OPERATION_TYPE.DOCUMENTS &&
			(op as IDocumentsOperation).config?.docs?.find((doc: IDocumentsDocs) => doc.type !== DOCS_TYPE.SCRIPTURE),
	) as IDocumentsOperation | undefined;
}

/**
 * CONNECTIONS sin evaluation y sin SCRIPTURE (se muestran como filas en DOCUMENTS).
 *
 * @param detail - Detalle del workflow
 */
export function findConnectionsWithoutEval(
	detail: Pick<IWorkflowDetailResponse, 'operations'> | undefined,
): IConnectionOperation[] {
	return (
		(detail?.operations?.filter(
			(op: IOperation) =>
				op.type === OPERATION_TYPE.CONNECTIONS &&
				(op as IConnectionOperation).config?.type !== CONNECTION_TYPE.SCRIPTURE &&
				!(op as IConnectionOperation).config?.evaluation,
		) as IConnectionOperation[]) ?? []
	);
}

/**
 * Docs `match_data` (switch Comparar datos): el API puede devolver string por legado;
 * normaliza a `boolean | null` para form y payload (NIF-1013).
 *
 * @param value - Valor crudo del API, form o legado `{ id }`
 */
export function normalizeDocsMatchData(value: unknown): boolean | null {
	if (value == null || value === '') {
		return null;
	}
	if (typeof value === 'boolean') {
		return value;
	}
	if (typeof value === 'object' && value !== null && 'id' in value) {
		return normalizeDocsMatchData((value as { id: unknown }).id);
	}
	if (typeof value === 'string') {
		const normalized = value.replace(/^"+|"+$/g, '').toLowerCase();
		if (normalized === 'false' || normalized === '0') {
			return false;
		}
		if (normalized === 'true' || normalized === '1') {
			return true;
		}
	}
	return Boolean(value);
}

/**
 * Número de doc opcional: NaN o 0 → `null` (mismo criterio legacy del payload).
 *
 * @param value - `total_tries` / `min` / `max` del form
 */
export function normalizeOptionalDocNumber(value: unknown): number | null {
	const n = Number(value);
	if (Number.isNaN(n) || n === 0) {
		return null;
	}
	return n;
}

/**
 * Tipos de documento ya usados en el FormArray (excluye GENERIC y, opcionalmente, la fila en edición).
 * Abrir cuando: modal DOCUMENTS necesita `prevSelectedDocs`.
 *
 * @param docs - `documentsArray.getRawValue()`
 * @param editingIndex - Índice en edición (se excluye su `type` de la lista)
 */
export function listPrevSelectedDocTypes(
	docs: Array<{ type?: string }> | null | undefined,
	editingIndex?: number,
): string[] {
	if (!Array.isArray(docs)) {
		return [];
	}
	return docs
		.filter((doc, index) => {
			if (doc.type === 'GENERIC') {
				return false;
			}
			if (editingIndex !== undefined) {
				return docs[editingIndex]?.type !== doc.type;
			}
			return true;
		})
		.map((doc) => String(doc.type));
}
